// src/pages/Leads/CreateLead.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Skeleton
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useDispatch } from 'react-redux';
import {
  getLeadsStatus,
  getLeadSources,
  getLeadBrands,
  createLead,
  getLeadById,
  updateLead,
  getLeadAssignedUsers,
} from '../../store/slices/leadsSlice';
import { getUsersByRole } from '../../store/slices/userSlice';
import { extractList } from '../../utils/response';
import { useAuth } from '../../hooks/useAuth';

const CreateLead = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { user, hasPermission } = useAuth();
  const canViewList = hasPermission('user-create');
  const isEditMode = Boolean(id);
  const isViewMode = window.location.pathname.includes('/view/') && !window.location.pathname.includes('/edit/');

  const [formData, setFormData] = useState({
    dba_name: '',
    name: '',
    email: '',
    phone: '',
    address: '', // Added address field
    description: '',
    status_id: '',
    source_id: '',
    brand_id: '',
    assignee_ids: user?.id ? [user.id] : []
  });

  const [errors, setErrors] = useState({});
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [frontSellerOptions, setFrontsellerOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch lead data after options are loaded
  useEffect(() => {
    if ((isEditMode || isViewMode) && optionsLoaded) {
      fetchLeadData();
    }
  }, [isEditMode, isViewMode, optionsLoaded]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchStatusData(),
        fetchUser(),
        fetchSourceLead(),
        fetchBrandsLeads()
      ]);
      setOptionsLoaded(true);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadData = async () => {
    try {
      setLoading(true);
      setDataLoaded(false);
      const response = await dispatch(getLeadById(id)).unwrap();

      if (response && response.success) {
        const leadData = response.data ?? response;
        let assigneeIds = [];
        if (leadData?.last_status_log?.assignee?.id) {
          assigneeIds = [leadData.last_status_log.assignee.id];
        }
        try {
          const assignedRes = await dispatch(getLeadAssignedUsers(id)).unwrap();
          const logs = assignedRes?.data?.data?.status_logs ?? assignedRes?.data?.status_logs ?? assignedRes?.status_logs ?? [];
          const ids = [...new Set(logs.map((s) => s.assignee?.id ?? s.assignee?.uuid ?? s.user_id).filter(Boolean))];
          if (ids.length) assigneeIds = ids;
        } catch (_) {
          // keep assigneeIds from last_status_log if fetch fails
        }
        setFormData({
          dba_name: leadData.dba_name || '',
          name: leadData.name || '',
          email: leadData.email || '',
          phone: leadData.phone || '',
          address: leadData.address || '',
          description: leadData.description || '',
          status_id: leadData.last_status_log?.status?.id || '',
          source_id: leadData.source?.id || '',
          brand_id: leadData.brand?.id || '',
          assignee_ids: assigneeIds
        });
        setDataLoaded(true);
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusData = async () => {
    try {
      const params = { 'conditions[model]': 'Lead' };
      const response = await dispatch(getLeadsStatus(params)).unwrap();
      const rawList = extractList(response);
      const statusList = (Array.isArray(rawList) ? rawList : []).map(status => ({
        id: status.id,
        name: status.name
      }));
      setStatusOptions(statusList);
    } catch (error) {
      console.error('Error fetching status data:', error);
    }
  };

  const fetchUser = async () => {
    if (!canViewList) return;
    try {
      const response = await dispatch(getUsersByRole('Sales User')).unwrap();
      const raw = response?.data ?? response;
      const list = Array.isArray(raw) ? raw : extractList(raw);
      const SalesUserList = (Array.isArray(list) ? list : []).map((users) => ({
        id: users?.uuid ?? users?.id,
        name: users?.name
      }));
      setFrontsellerOptions(SalesUserList);
    } catch (error) {
      console.error('Error fetching users data:', error);
    }
  }

  const fetchSourceLead = async () => {
    try {
      const response = await dispatch(getLeadSources()).unwrap();
      const rawList = extractList(response);
      const getSources = (Array.isArray(rawList) ? rawList : []).map(source => ({
        id: source?.id,
        name: source?.name
      }));
      setSourceOptions(getSources);
    } catch (error) {
      console.error('Error fetching source data:', error);
    }
  }

  const fetchBrandsLeads = async () => {
    try {
      const response = await dispatch(getLeadBrands()).unwrap();
      const rawList = extractList(response);
      const getbrandLeads = (Array.isArray(rawList) ? rawList : []).map(brand => ({
        id: brand?.id,
        name: brand?.name
      }));
      setBrandOptions(getbrandLeads);
    } catch (error) {
      console.error('Error fetching brands data:', error);
    }
  }

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleAssigneesChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      assignee_ids: value ? value.map((v) => v.id) : []
    }));

    if (errors.assignee_ids) {
      setErrors(prev => ({
        ...prev,
        assignee_ids: ''
      }));
    }
  };

  // Find the selected assignees from frontSellerOptions
  const getSelectedAssignees = () => {
    return (formData.assignee_ids || []).map(
      (id) => frontSellerOptions.find((s) => s.id === id)
    ).filter(Boolean);
  };

  // Helper function to get display value for selects in view mode
  const getDisplayValue = (options, selectedId) => {
    const selectedOption = options.find(option => option.id === selectedId);
    return selectedOption ? selectedOption.name : 'N/A';
  };

  const getAssigneesDisplayValue = () => {
    const selected = getSelectedAssignees();
    if (!selected.length) return 'N/A';
    return selected.map((s) => s.name).join(', ');
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.dba_name.trim()) newErrors.dba_name = 'DBA Name is required';
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.status_id) newErrors.status_id = 'Status is required';
    if (!formData.source_id) newErrors.source_id = 'Source is required';
    if (!formData.brand_id) newErrors.brand_id = 'Brand is required';
    if (!formData.assignee_ids?.length) newErrors.assignee_ids = 'At least one assignee is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      try {
        setSubmitting(true);

        const payload = {
          dba_name: formData.dba_name,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          description: formData.description,
          status_id: formData.status_id,
          source_id: formData.source_id,
          brand_id: formData.brand_id,
          assignees: formData.assignee_ids
        };

        let response;
        if (isEditMode) {
          response = await dispatch(updateLead({ id, data: payload })).unwrap();
        } else {
          response = await dispatch(createLead(payload)).unwrap();
        }

        if (response.success) {
          navigate('/leads');
        } else {
          console.error(`Failed to ${isEditMode ? 'update' : 'create'} lead:`, response.message);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} lead:`, error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    navigate('/leads');
  };

  const handleEdit = () => {
    navigate(`/leads/edit/${id}`);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length > 10) value = value.slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  // All fields are disabled in view mode or during loading
  const isDisabled = isViewMode || loading;

  // Skeleton loading component for form fields
  const renderSkeletonField = (width = '100%') => (
    <Skeleton variant="rectangular" width={width} height={40} animation="wave" />
  );

  const renderSkeletonSelect = () => (
    <Skeleton variant="rectangular" width="100%" height={56} animation="wave" />
  );

  const renderSkeletonTextArea = () => (
    <Skeleton variant="rectangular" width="100%" height={80} animation="wave" />
  );

  // View mode styling
  const viewModeTitleStyle = {
    color: 'text.secondary',
    fontWeight: 600,
    mb: 1
  };

  const viewModeFieldStyle = {
    p: 1.5,
    border: '1px solid',
    borderColor: 'grey.300',
    borderRadius: 1,
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'grey.50',
    color: 'text.primary'
  };

  const viewModeTextAreaStyle = {
    ...viewModeFieldStyle,
    minHeight: '80px',
    alignItems: 'flex-start'
  };

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header with Skeleton */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {loading ? (
              <Skeleton variant="text" width={200} height={40} animation="wave" />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  color: isViewMode ? 'text.secondary' : 'blue.600',
                  fontWeight: 600,
                  mb: 1,
                  opacity: isViewMode ? 0.7 : 1
                }}
              >
                {isViewMode ? 'View Lead' : isEditMode ? 'Edit Lead' : 'Create New Lead'}
              </Typography>
            )}

            {isViewMode && !loading && (
              <Button
                variant="contained"
                onClick={handleEdit}
                sx={{ bgcolor: 'blue.600', '&:hover': { bgcolor: 'blue.700' } }}
              >
                Edit Lead
              </Button>
            )}
            {isViewMode && loading && (
              <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            <Card sx={{
              boxShadow: isViewMode ? 1 : 2,
              border: isViewMode ? '1px solid' : 'none',
              borderColor: isViewMode ? 'grey.300' : 'transparent'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonField()
                    ) : isViewMode ? (
                      <>
                        <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                          DBA Name
                        </Typography>
                        <Typography variant="body1" sx={viewModeFieldStyle}>
                          {formData.dba_name || 'N/A'}
                        </Typography>
                      </>
                    ) : (
                      <TextField
                        label="DBA Name"
                        fullWidth
                        placeholder="Enter DBA name (Doing Business As)"
                        value={formData.dba_name}
                        onChange={handleChange('dba_name')}
                        error={!!errors.dba_name}
                        helperText={errors.dba_name}
                        disabled={isDisabled}
                      />
                    )}
                  </Grid>
                  {/* Name Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonField()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.name || 'N/A'}
                      </Typography>
                    ) : (
                      <TextField
                        label="Enter lead name"
                        fullWidth
                        placeholder="Enter lead name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        error={!!errors.name}
                        helperText={errors.name}
                        disabled={isDisabled}
                      />
                    )}
                  </Grid>

                  {/* Status Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {getDisplayValue(statusOptions, formData.status_id)}
                      </Typography>
                    ) : (
                      <FormControl fullWidth error={!!errors.status_id} disabled={isDisabled}>
                        <InputLabel>Select status</InputLabel>
                        <Select
                          value={formData.status_id}
                          onChange={handleChange('status_id')}
                          label="Select status"
                        >
                          <MenuItem value="" disabled>
                            Select status <span>*</span>
                          </MenuItem>
                          {statusOptions.map((status) => (
                            <MenuItem key={status.id} value={status.id}>
                              {status.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.status_id && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                            {errors.status_id}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  </Grid>

                  {/* Email Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonField()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.email || 'N/A'}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange('email')}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={isDisabled}
                      />
                    )}
                  </Grid>

                  {/* Phone Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonField()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {formData.phone || 'N/A'}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        label="Phone"
                        type='tel'
                        placeholder="1234567890"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        disabled={isDisabled}
                        inputProps={{
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          maxLength: 15
                        }}
                      />
                    )}
                  </Grid>

                  {/* Address Field - NEW FIELD ADDED */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonField()
                    ) : isViewMode ? (
                      <>
                        <Typography variant="subtitle1" sx={viewModeTitleStyle}>
                          Address
                        </Typography>
                        <Typography variant="body1" sx={viewModeFieldStyle}>
                          {formData.address || 'N/A'}
                        </Typography>
                      </>
                    ) : (
                      <TextField
                        fullWidth
                        label="Address"
                        placeholder="Enter complete address"
                        value={formData.address}
                        onChange={handleChange('address')}
                        disabled={isDisabled}
                        multiline
                        rows={2}
                      />
                    )}
                  </Grid>

                  {/* Description Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonTextArea()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeTextAreaStyle}>
                        {formData.description || 'N/A'}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={3}
                        placeholder="Enter description"
                        value={formData.description}
                        onChange={handleChange('description')}
                        disabled={isDisabled}
                      />
                    )}
                  </Grid>

                  {/* Brand Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {getDisplayValue(brandOptions, formData.brand_id)}
                      </Typography>
                    ) : (
                      <FormControl fullWidth error={!!errors.brand_id} disabled={isDisabled}>
                        <InputLabel>Select brand</InputLabel>
                        <Select
                          value={formData.brand_id}
                          onChange={handleChange('brand_id')}
                          label="Select brand"
                        >
                          <MenuItem value="" disabled>Select brand</MenuItem>
                          {brandOptions.map((brand) => (
                            <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  {/* Source Field */}
                  <Grid item xs={12} md={6}>
                    {loading ? (
                      renderSkeletonSelect()
                    ) : isViewMode ? (
                      <Typography variant="body1" sx={viewModeFieldStyle}>
                        {getDisplayValue(sourceOptions, formData.source_id)}
                      </Typography>
                    ) : (
                      <FormControl fullWidth error={!!errors.source_id} disabled={isDisabled}>
                        <InputLabel>Select source</InputLabel>
                        <Select
                          value={formData.source_id}
                          onChange={handleChange('source_id')}
                          label="Select source"
                        >
                          <MenuItem value="" disabled>Select source</MenuItem>
                          {sourceOptions.map((source) => (
                            <MenuItem key={source.id} value={source.id}>{source.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>

                  {/* Lead Assign To Field - multiple assignees */}
                  {canViewList &&
                    <Grid item xs={12} md={6}>
                      {loading ? (
                        renderSkeletonSelect()
                      ) : isViewMode ? (
                        <Typography variant="body1" sx={viewModeFieldStyle}>
                          {getAssigneesDisplayValue()}
                        </Typography>
                      ) : (
                        <FormControl fullWidth>
                          <Autocomplete
                            multiple
                            options={frontSellerOptions}
                            getOptionLabel={(option) => option.name}
                            value={getSelectedAssignees()}
                            onChange={handleAssigneesChange}
                            disabled={isDisabled}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search and select assignees"
                                error={!!errors.assignee_ids}
                                label="Select assignees"
                              />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="No assignee found"
                          />
                          {errors.assignee_ids && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                              {errors.assignee_ids}
                            </Typography>
                          )}
                        </FormControl>
                      )}
                    </Grid>
                  }
                </Grid>
              </CardContent>
            </Card>

            {/* Action Buttons with Skeletons */}
            {!isViewMode && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                {loading ? (
                  <>
                    <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
                    <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ minWidth: '120px' }}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ minWidth: '120px', bgcolor: 'blue.600', '&:hover': { bgcolor: 'blue.700' } }}
                      disabled={submitting}
                    >
                      {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Lead' : 'Create Lead')}
                    </Button>
                  </>
                )}
              </Box>
            )}

            {/* Back Button for View Mode with Skeleton */}
            {isViewMode && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                {loading ? (
                  <Skeleton variant="rectangular" width={120} height={36} animation="wave" />
                ) : (
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{ minWidth: '120px' }}
                  >
                    Back to Leads
                  </Button>
                )}
              </Box>
            )}
          </form>
      </Container>
    </PageLayout>
  );
};

export default CreateLead;